// TEMPORARY diagnostic. Admin-only. No PII (no names, no emails, no amounts).
// Enable by appending ?diag=enrollment to /admin. Remove after root cause is
// confirmed and the smallest repair is landed.

import { useEffect, useState } from "react";
import { fetchEnrollmentData, sessionLabelMatches, parseSelectedSessions, countsAsEnrolled, enrollmentFor, isPaid, type RegistrationRow, type RegistrationChildRow } from "@/lib/enrollment";
import { getClassAliases } from "@/lib/classIdentity";

interface Target {
  label: string;
  slug: string;
  registryUuid: string;
  historicalUuid: string;
  session: string;
  expected: string;
}

const TARGETS: Target[] = [
  { label: "All About Farming", slug: "all-about-farming", registryUuid: "a7c69675-a7b0-447a-9e0c-ae3883b63a64", historicalUuid: "5359fc38-5036-4684-9794-4a103ea70555", session: "Session 3", expected: "~2" },
  { label: "Chess Club", slug: "chess-club", registryUuid: "20509fd8-ef41-413b-b67b-a1b3ae8d3444", historicalUuid: "1dd3fc22-c4b0-4f1a-b7a7-3705045e386d", session: "Session 3", expected: "~2" },
  { label: "Mommy & Me Crochet", slug: "mommy-me-crochet", registryUuid: "6738e837-7993-4389-a0a5-22e0db1021ff", historicalUuid: "d9cbe2a8-89eb-4aa8-bfab-6463a36045ec", session: "Session 3", expected: "~4 + manual" },
  { label: "Pokémon Club", slug: "pokemon-club", registryUuid: "792d29ea-0052-4ed1-8d4e-7bab9bd4d347", historicalUuid: "128e5477-cc97-4509-a320-62cb6e95d31b", session: "Session 3", expected: "~5" },
  { label: "LEGO Robotics", slug: "lego-robotics", registryUuid: "f61ee879-6fc0-42e1-9eae-dfe9145550b4", historicalUuid: "bb111111-1111-4111-8111-000000000002", session: "Session 3", expected: "~2" },
];

function tail(id: string | null | undefined) {
  const s = String(id ?? "");
  return s.length > 8 ? "…" + s.slice(-6) : s;
}

export function EnrollmentDiagnostic() {
  const [state, setState] = useState<{ loading: boolean; error: string | null; payments: RegistrationRow[]; children: RegistrationChildRow[] }>({
    loading: true, error: null, payments: [], children: [],
  });

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchEnrollmentData();
        setState({ loading: false, error: null, payments: d.payments, children: d.children });
      } catch (e: any) {
        setState({ loading: false, error: e?.message ?? String(e), payments: [], children: [] });
      }
    })();
  }, []);

  if (state.loading) return <div className="mt-6 text-xs text-muted-foreground">Loading diagnostic…</div>;
  if (state.error) return <div className="mt-6 text-xs text-destructive">Diagnostic error: {state.error}</div>;

  return (
    <section className="mt-10 rounded-lg border border-dashed border-amber-500/50 bg-amber-50/40 p-4">
      <h2 className="text-sm font-semibold text-amber-900">Enrollment Diagnostic (admin-only, temporary)</h2>
      <p className="text-[11px] text-amber-900/70 mb-4">
        Total fetched: {state.payments.length} payments, {state.children.length} child rows. No PII shown.
      </p>

      <div className="space-y-6">
        {TARGETS.map((t) => (
          <ClassBlock key={t.slug} target={t} payments={state.payments} children={state.children} />
        ))}
      </div>
    </section>
  );
}

function ClassBlock({ target, payments, children }: { target: Target; payments: RegistrationRow[]; children: RegistrationChildRow[] }) {
  const aliases = getClassAliases(target.slug);
  const aliasSet = new Set(aliases);

  // Candidate payments: match slug, registry uuid, or historical uuid (with db: variants).
  const idPool = new Set<string>([
    target.slug, `db:${target.slug}`,
    target.registryUuid, `db:${target.registryUuid}`,
    target.historicalUuid, `db:${target.historicalUuid}`,
  ]);
  const candidates = payments.filter((p) => idPool.has(String(p.class_id)) || aliasSet.has(String(p.class_id)));

  const paymentById = new Map(payments.map((p) => [p.id, p]));

  // Matching child rows for these candidate parents.
  const childRows = children.filter((c) => candidates.some((p) => p.id === c.class_payment_id));
  const parentsWithChildren = new Set(childRows.map((c) => c.class_payment_id));

  const finalCount = enrollmentFor(payments, target.slug, target.session, children);

  return (
    <div className="rounded-md border border-amber-300/60 bg-white p-3 text-[11px]">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
        <span className="text-sm font-semibold">{target.label}</span>
        <span className="text-muted-foreground">slug: <code>{target.slug}</code></span>
        <span className="text-muted-foreground">session: <code>{target.session}</code></span>
        <span className="text-muted-foreground">expected: {target.expected}</span>
        <span className="font-mono px-2 py-0.5 rounded bg-amber-100 text-amber-900">
          resolver → {finalCount}
        </span>
      </div>

      <div className="mb-2">
        <span className="font-semibold">Aliases returned by getClassAliases:</span>{" "}
        <code className="break-all">{aliases.join(", ") || "(none)"}</code>
      </div>

      <div className="mb-2">
        <span className="font-semibold">Candidate payments (matched by slug/registryUuid/historicalUuid/alias):</span>
        {candidates.length === 0 ? (
          <p className="text-muted-foreground italic">none in fetched dataset</p>
        ) : (
          <table className="w-full mt-1 border-collapse">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="pr-2">pmt id</th>
                <th className="pr-2">class_id</th>
                <th className="pr-2">alias hit?</th>
                <th className="pr-2">status</th>
                <th className="pr-2">hidden</th>
                <th className="pr-2">selected_sessions</th>
                <th className="pr-2">session match?</th>
                <th className="pr-2">has child rows?</th>
                <th className="pr-2">decision</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((p) => {
                const aliasHit = aliasSet.has(String(p.class_id));
                const labels = parseSelectedSessions(p.selected_sessions);
                const sessionHit = labels.some((l) => sessionLabelMatches(l, target.session));
                const hasKids = parentsWithChildren.has(p.id);
                const paid = isPaid(p.payment_status);
                const counts = countsAsEnrolled(p);

                let decision = "counted";
                if (!aliasHit) decision = "excluded: alias miss";
                else if (!counts) decision = `excluded: ${p.hidden ? "hidden" : "not paid (" + (p.payment_status ?? "null") + ")"}`;
                else if (hasKids) decision = "excluded: superseded by child rows";
                else if (!sessionHit) decision = `excluded: no label matches ${target.session}`;

                return (
                  <tr key={p.id} className="border-t border-amber-200/60">
                    <td className="pr-2 font-mono">{tail(p.id)}</td>
                    <td className="pr-2 font-mono break-all">{p.class_id}</td>
                    <td className="pr-2">{aliasHit ? "yes" : "no"}</td>
                    <td className="pr-2">{p.payment_status ?? "null"}</td>
                    <td className="pr-2">{String(!!p.hidden)}</td>
                    <td className="pr-2 font-mono">{labels.length ? labels.join(" | ") : "—"}</td>
                    <td className="pr-2">{sessionHit ? "yes" : "no"}</td>
                    <td className="pr-2">{hasKids ? "yes" : "no"}</td>
                    <td className="pr-2">{decision}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {childRows.length > 0 && (
        <div>
          <span className="font-semibold">Child rows on candidate parents:</span>
          <table className="w-full mt-1 border-collapse">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="pr-2">child id</th>
                <th className="pr-2">parent pmt</th>
                <th className="pr-2">child.class_id</th>
                <th className="pr-2">session_label</th>
                <th className="pr-2">alias hit?</th>
                <th className="pr-2">session match?</th>
                <th className="pr-2">parent counts?</th>
                <th className="pr-2">decision</th>
              </tr>
            </thead>
            <tbody>
              {childRows.map((c) => {
                const parent = paymentById.get(c.class_payment_id);
                const childAliasHit = c.class_id ? aliasSet.has(String(c.class_id)) : false;
                const parentAliasHit = parent ? aliasSet.has(String(parent.class_id)) : false;
                const aliasHit = childAliasHit || parentAliasHit;
                const sessionHit = sessionLabelMatches(c.session_label, target.session);
                const parentCounts = parent ? countsAsEnrolled(parent) : false;
                let decision = "counted";
                if (!parent) decision = "excluded: orphan child";
                else if (!parentCounts) decision = `excluded: parent ${parent.hidden ? "hidden" : "not paid"}`;
                else if (!aliasHit) decision = "excluded: alias miss (child+parent)";
                else if (!sessionHit) decision = `excluded: session_label "${c.session_label}" ≠ ${target.session}`;
                return (
                  <tr key={c.id} className="border-t border-amber-200/60">
                    <td className="pr-2 font-mono">{tail(c.id)}</td>
                    <td className="pr-2 font-mono">{tail(c.class_payment_id)}</td>
                    <td className="pr-2 font-mono break-all">{c.class_id ?? "—"}</td>
                    <td className="pr-2 font-mono">{c.session_label ?? "—"}</td>
                    <td className="pr-2">{aliasHit ? "yes" : "no"}</td>
                    <td className="pr-2">{sessionHit ? "yes" : "no"}</td>
                    <td className="pr-2">{parentCounts ? "yes" : "no"}</td>
                    <td className="pr-2">{decision}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
